import { ReactNode } from "react";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import {
  CaseStatus,
  ExceptionMutationAction,
  FindingException,
  TicketRefType,
} from "~/operations/types";
import { ticketTypeIconMap } from "~/pages/tickets/ticket/TicketPage/utils/ticketPageUtils";
import { AggregateScoresNodeCasesEdge } from "~/components/FirewatchPage";
import { getTicketDetailsUrl } from "~/lib/ticketUtils";
import { SpaceOrWorkspaceScope, SpaceScope } from "~/hooks/useScope";
import { getExceptionDetailUrl } from "~/lib/urlUtils";
import { FalsePositiveIcon, WorkaroundIcon } from "~/icons";

export function formatEffortValue(effort: number): string {
  const hours = Math.floor(effort / 60);
  const minutes = effort % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${minutes}m`;
  }
}

export function formatFindingsCount(count: number): string {
  if (count > 99999) {
    return `${Math.round(count / 1000)}K`;
  }
  return count.toLocaleString();
}

type Status = {
  icon: ReactNode;
  label: string;
  url?: string;
  genericLabel?: string;
};

function getExceptionStatus(
  exception?: Pick<
    FindingException,
    "action" | "exceptionId" | "createdAt"
  > | null,
  scope?: SpaceScope,
): Status | null {
  const url = getExceptionDetailUrl({
    spaceId: scope?.id ?? "",
    exceptionId: exception?.exceptionId ?? "",
  });

  switch (exception?.action) {
    case ExceptionMutationAction.RiskAccepted:
      return {
        icon: <ThumbUpOffAltIcon />,
        label: "Risk Accepted",
        genericLabel: "View Exception",
        url,
      };
    case ExceptionMutationAction.Workaround:
      return {
        icon: <WorkaroundIcon />,
        label: "Workaround",
        genericLabel: "View Exception",
        url,
      };
    case ExceptionMutationAction.FalsePositive:
      return {
        icon: <FalsePositiveIcon />,
        label: "False Positive",
        genericLabel: "View Exception",
        url,
      };
    case ExceptionMutationAction.Disable:
      return {
        icon: <BlockOutlinedIcon />,
        label: "Disable",
        genericLabel: "View Exception",
        url,
      };
    default:
      return null;
  }
}

function getTicketStatus(
  ticket: AggregateScoresNodeCasesEdge,
  scope?: SpaceOrWorkspaceScope,
): Status {
  const ticketType =
    ticket?.node?.ticketRefs?.[0]?.type || TicketRefType.Unknown;
  const Icon = ticketTypeIconMap[ticketType];
  const url = getTicketDetailsUrl({ scope, ticketMrn: ticket.node.mrn });

  return {
    icon: <Icon />,
    label: "Ticket",
    genericLabel: "View Ticket",
    url,
  };
}

export function getStatusDisplay({
  exception,
  cases,
  scope,
  spaceScope,
}: {
  exception?: Pick<
    FindingException,
    "action" | "createdAt" | "exceptionId"
  > | null | null;
  cases?: AggregateScoresNodeCasesEdge[] | null;
  scope?: SpaceOrWorkspaceScope;
  spaceScope?: SpaceScope;
}): Status | null {
  // Find the most recent open ticket (if any)
  const ticket = cases
    ?.filter((edge) => edge.node.status === CaseStatus.Open)
    ?.sort((a, b) => {
      const aDate = new Date(a.node.createdAt ?? 0).getTime();
      const bDate = new Date(b.node.createdAt ?? 0).getTime();
      return bDate - aDate;
    })[0];

  const exceptionCreatedAt = exception?.createdAt
    ? new Date(exception.createdAt).getTime()
    : null;
  const ticketCreatedAt = ticket?.node?.createdAt
    ? new Date(ticket.node.createdAt).getTime()
    : null;

  const isTicketExist = ticketCreatedAt && ticket;
  const isTicketCreatedLaterThanException = Boolean(
    ticketCreatedAt &&
      (!exceptionCreatedAt || ticketCreatedAt >= exceptionCreatedAt),
  );

  if (isTicketExist && isTicketCreatedLaterThanException) {
    return getTicketStatus(ticket, scope);
  }

  const exceptionStatus = getExceptionStatus(exception, spaceScope);
  if (exceptionStatus && exceptionCreatedAt) {
    return exceptionStatus;
  }

  return null;
}
